/**
 * IOSSwitch (web)
 *
 * Drop-in replacement for `react-native`'s `<Switch>` on web/Electron. The
 * default `react-native-web` Switch renders as a Material-style toggle (thin
 * track, thumb extending past the rails) which clashes with the iOS visual
 * vocabulary the rest of the app uses. This component renders the iconic iOS
 * pill toggle so desktop matches the native iPhone build.
 *
 * Prop-compatible with RN's Switch:
 *   value             — current on/off state
 *   onValueChange     — (next: boolean) => void
 *   disabled          — disables interaction; dims the track
 *   trackColor        — { false?: string; true?: string }
 *   thumbColor        — single thumb fill (defaults to white)
 *   ios_backgroundColor — alias for trackColor.false (matches RN's iOS-only prop)
 *   style             — passthrough ViewStyle
 *
 * Wired in via the `react-native` alias in BOTH bundlers:
 *   - Electron / desktop: `desktop/webpack.config.js`
 *   - Expo for web (Metro): `metro.config.js` → WEB_STUBS map
 * Both routes point `react-native` at `react-native-web-extended.js`, which
 * re-exports react-native-web with `Switch` overridden to this component.
 * Native iOS/Android still use the system Switch, so this file is web-only.
 */

import React, { useEffect, useRef } from 'react';
import { Animated, Pressable, StyleSheet, Easing } from 'react-native-web';

// iOS dimensions taken from UIKit defaults so the visual reads as a true
// iOS switch, not a smaller imitation. Track 51×31, thumb 27 with a 2px
// padding inset so it sits centered on rest and doesn't overlap the rim.
const TRACK_WIDTH  = 51;
const TRACK_HEIGHT = 31;
const THUMB_SIZE   = 27;
const PADDING      = 2;
const THUMB_TRAVEL = TRACK_WIDTH - THUMB_SIZE - PADDING * 2;

const DEFAULT_OFF_COLOR = '#E9E9EA'; // iOS light-mode off track
const DEFAULT_ON_COLOR  = '#34C759'; // iOS system green

export default function Switch({
  value,
  onValueChange,
  disabled = false,
  trackColor,
  thumbColor,
  ios_backgroundColor,
  style,
  testID,
  accessibilityLabel,
}) {
  // Single 0..1 driver for both thumb position and track tint. Holding the
  // initial value in a ref means a parent re-render that recreates the
  // Animated.Value isn't possible — useRef keeps the same instance for the
  // life of the component.
  const progress = useRef(new Animated.Value(value ? 1 : 0)).current;

  useEffect(() => {
    Animated.timing(progress, {
      toValue: value ? 1 : 0,
      duration: 180,
      easing: Easing.bezier(0.4, 0.0, 0.2, 1), // Material-ish smoothing reads cleanly on iOS too
      useNativeDriver: false, // backgroundColor isn't natively-driveable on RN-Web
    }).start();
  }, [value, progress]);

  const offColor = (trackColor && trackColor.false) || ios_backgroundColor || DEFAULT_OFF_COLOR;
  const onColor  = (trackColor && trackColor.true)  || DEFAULT_ON_COLOR;

  const trackBg = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [offColor, onColor],
  });

  const thumbX = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [0, THUMB_TRAVEL],
  });

  const handlePress = () => {
    if (disabled) return;
    onValueChange?.(!value);
  };

  return (
    <Pressable
      onPress={handlePress}
      disabled={disabled}
      style={[styles.wrapper, disabled && styles.disabled, style]}
      accessibilityRole="switch"
      accessibilityState={{ checked: !!value, disabled: !!disabled }}
      accessibilityLabel={accessibilityLabel}
      testID={testID}
    >
      <Animated.View style={[styles.track, { backgroundColor: trackBg }]}>
        <Animated.View
          style={[
            styles.thumb,
            { backgroundColor: thumbColor || '#FFFFFF', transform: [{ translateX: thumbX }] },
          ]}
        />
      </Animated.View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    width: TRACK_WIDTH,
    height: TRACK_HEIGHT,
    // Settings rows are flex rows; without flexShrink:0 the wrapper can
    // get squeezed when the label text is long and the row hits its width.
    flexShrink: 0,
    flexGrow: 0,
    // RN-Web Pressable uses `cursor: pointer` already; nothing extra needed.
  },
  disabled: {
    opacity: 0.5,
  },
  track: {
    width: TRACK_WIDTH,
    height: TRACK_HEIGHT,
    borderRadius: TRACK_HEIGHT / 2,
    padding: PADDING,
    justifyContent: 'center',
    // NOTE: deliberately NOT overflow:hidden — the iOS thumb has a soft
    // drop-shadow that extends ~3px below the track rim, which is what
    // gives the toggle its "thumb floats above track" silhouette.
  },
  thumb: {
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: THUMB_SIZE / 2,
    // Same flex-row guard as the wrapper — the thumb is itself a child of
    // a row-direction track in RN-Web's compiled output.
    flexShrink: 0,
    flexGrow: 0,
    // The drop shadow on the thumb is what sells the "iOS toggle" silhouette
    // — without it the thumb looks pasted onto the track.
    boxShadow:
      '0 3px 1px rgba(0, 0, 0, 0.06), 0 3px 8px rgba(0, 0, 0, 0.15), 0 0 0 0.5px rgba(0, 0, 0, 0.04)',
  },
});
